import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getEventByCode, createSuggestion } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Music, CheckCircle, Sparkles } from 'lucide-react';
import LoadingScreen from '../components/ui/LoadingScreen';

const GuestInterface = () => {
  const { accessCode } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    guest_name: '',
    requested_title: '',
    requested_artist: '',
    link: ''
  });

  useEffect(() => {
    loadEvent();
  }, [accessCode]);

  const loadEvent = async () => {
    try {
      const response = await getEventByCode(accessCode);
      setEvent(response.data);
    } catch (error) {
      toast.error('Événement introuvable');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await createSuggestion({
        event_id: event.id,
        ...formData
      });
      
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          guest_name: '',
          requested_title: '',
          requested_artist: '',
          link: ''
        });
      }, 3000);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de votre suggestion');
    }
  };

  if (loading) {
    return <LoadingScreen message="Chargement de l'événement..." />;
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Événement introuvable</h2>
          <p className="text-sunset-orange/70">Vérifiez le lien ou le QR code</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900/30 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sunset-orange to-sunset-secondary rounded-3xl mb-6 shadow-2xl shadow-purple-500/50 animate-pulse">
            <Music className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {event.name}
          </h1>
          <div className="flex items-center justify-center gap-2 text-sunset-orange/80">
            <Sparkles className="w-4 h-4" />
            <p className="text-sm">
              Proposez vos musiques préférées
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-dark-card/70 backdrop-blur-2xl border border-sunset-orange/30 rounded-3xl p-8 shadow-2xl shadow-purple-500/10">
          {submitted ? (
            <div className="text-center py-12" data-testid="success-message">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Merci !</h3>
              <p className="text-sunset-orange/80 text-lg">
                Le DJ verra votre demande 🙏
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="guest-name" className="text-text-primary text-sm mb-2 block font-medium">
                  Votre pseudo
                </Label>
                <Input
                  id="guest-name"
                  data-testid="guest-name-input"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  className="bg-dark-input/60 border-sunset-orange/40 text-white placeholder:text-sunset-orange/40 focus:border-purple-400 focus:ring-purple-400/30 h-14 text-lg"
                  placeholder="Alex"
                  required
                />
              </div>

              <div>
                <Label htmlFor="track-title" className="text-text-primary text-sm mb-2 block font-medium">
                  Titre de la musique
                </Label>
                <Input
                  id="track-title"
                  data-testid="track-title-input"
                  value={formData.requested_title}
                  onChange={(e) => setFormData({ ...formData, requested_title: e.target.value })}
                  className="bg-dark-input/60 border-sunset-orange/40 text-white placeholder:text-sunset-orange/40 focus:border-purple-400 focus:ring-purple-400/30 h-14 text-lg"
                  placeholder="One More Time"
                  required
                />
              </div>

              <div>
                <Label htmlFor="artist-name" className="text-text-primary text-sm mb-2 block font-medium">
                  Artiste (optionnel)
                </Label>
                <Input
                  id="artist-name"
                  data-testid="artist-name-input"
                  value={formData.requested_artist}
                  onChange={(e) => setFormData({ ...formData, requested_artist: e.target.value })}
                  className="bg-dark-input/60 border-sunset-orange/40 text-white placeholder:text-sunset-orange/40 focus:border-purple-400 focus:ring-purple-400/30 h-14 text-lg"
                  placeholder="Daft Punk"
                />
              </div>

              <div>
                <Label htmlFor="music-link" className="text-text-primary text-sm mb-2 block font-medium">
                  Lien YouTube/Spotify (optionnel)
                </Label>
                <Input
                  id="music-link"
                  data-testid="music-link-input"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="bg-dark-input/60 border-sunset-orange/40 text-white placeholder:text-sunset-orange/40 focus:border-purple-400 focus:ring-purple-400/30 h-14 text-lg"
                  placeholder="https://..."
                />
              </div>

              <Button
                data-testid="submit-suggestion-button"
                type="submit"
                className="w-full h-14 text-lg bg-gradient-to-r from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-2xl shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 transition-all duration-300"
              >
                Envoyer ma suggestion
              </Button>
            </form>
          )}
        </div>

        {/* Info Footer */}
        <div className="mt-6 text-center">
          <p className="text-sunset-orange/50 text-sm">
            Vos suggestions seront visibles par le DJ en temps réel
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestInterface;
